import * as THREE from "three";

export class LaserManager {
  constructor(scene, blockManager) {
    this.scene = scene;
    this.blockManager = blockManager;
    this.lasers = [];

    // Shared laser timing for synchronized pulsing
    // Laser timing adjusted for deltaGameSpeed units (normalized for 60fps)
    this.onDuration = 30; // 30 normalized frames = 500ms at 60fps
    this.offDuration = 30; // 30 normalized frames = 500ms at 60fps
    this.totalCycleDuration = this.onDuration + this.offDuration;

    // Global phase shared by all lasers for synchronization
    this.globalPhase = 0;

    // Shared geometry + materials reused by every laser. Beam and both glow
    // layers are fixed cell size; the tall top-row beams are produced via
    // mesh.scale (see LevelGenerator), not a per-laser geometry. Sparks share a
    // unit geometry scaled per spark. Disposed once in dispose().
    this.beamWidth = 0.1; // 10px wide beam
    this.beamHeight = 0.5; // cell height (50px)
    const innerGlowWidth = 0.15;
    const outerGlowWidth = 0.3; // 30px wide glow

    this.sharedBeamGeometry = new THREE.PlaneGeometry(
      this.beamWidth,
      this.beamHeight
    );
    this.sharedInnerGlowGeometry = new THREE.PlaneGeometry(
      innerGlowWidth,
      this.beamHeight
    );
    this.sharedOuterGlowGeometry = new THREE.PlaneGeometry(
      outerGlowWidth,
      this.beamHeight
    );

    // Horizontal beam geometries: the same beam/glow rotated 90°, so width is the
    // cell span (beamHeight) and the thin axis is across it. A `l` template cell
    // renders a horizontal beam segment; a row of them strung between two blocks
    // is a horizontal gate the player times the same way as a vertical `L`.
    this.sharedHBeamGeometry = new THREE.PlaneGeometry(
      this.beamHeight,
      this.beamWidth
    );
    this.sharedHInnerGlowGeometry = new THREE.PlaneGeometry(
      this.beamHeight,
      innerGlowWidth
    );
    this.sharedHOuterGlowGeometry = new THREE.PlaneGeometry(
      this.beamHeight,
      outerGlowWidth
    );

    this.sharedSparkGeometry = new THREE.PlaneGeometry(1, 1); // scaled per spark

    this.sharedBeamMaterial = new THREE.MeshStandardMaterial({
      color: 0xff0000,
      emissive: 0xff0000,
      emissiveIntensity: 1.5,
      transparent: true,
      opacity: 0.9,
      side: THREE.DoubleSide,
    });
    this.sharedInnerGlowMaterial = new THREE.MeshStandardMaterial({
      color: 0xff3333,
      emissive: 0xff3333,
      emissiveIntensity: 1.0,
      transparent: true,
      opacity: 0.6,
      side: THREE.DoubleSide,
    });
    this.sharedOuterGlowMaterial = new THREE.MeshStandardMaterial({
      color: 0xff7777,
      emissive: 0xff7777,
      emissiveIntensity: 0.4,
      transparent: true,
      opacity: 0.2,
      side: THREE.DoubleSide,
    });
    this.sharedSparkMaterial = new THREE.MeshStandardMaterial({
      color: 0xff0000,
      emissive: 0xff0000,
      emissiveIntensity: 2.0,
      transparent: true,
      opacity: 1.0,
      side: THREE.DoubleSide,
    });
  }

  reset() {
    // Dispose all lasers and their effects properly
    this.lasers.forEach((laser) => {
      this.disposeLaser(laser); // Use the helper for cleanup
    });

    // Clear lasers array
    this.lasers = [];

    // Reset global phase
    this.globalPhase = 0;
  }

  // Create a laser segment occupying a single grid cell
  createLaser(x, y, createSparks = false) {
    // Grid cell is 0.5 x 0.5 units (50px x 50px)
    const cellSize = 0.5;

    // Position the laser in the middle of its grid cell
    const centerX = x + cellSize / 2;
    const centerY = y + cellSize / 2;

    // Create the main laser beam - cell height (0.5 units), shared geometry/material
    const beamWidth = this.beamWidth;
    const beamHeight = this.beamHeight;

    const beam = new THREE.Mesh(this.sharedBeamGeometry, this.sharedBeamMaterial);
    beam.position.set(centerX, centerY, 0.02); // Position within cell
    beam.castShadow = false;
    beam.visible = true;
    this.scene.add(beam);

    // Create gradient glow effect from two shared layers (brightest inner,
    // dimmest outer), grouped so they pulse/extend together.
    const glowGroup = new THREE.Group();
    glowGroup.position.set(centerX, centerY, 0.01);
    glowGroup.visible = true;

    const innerGlow = new THREE.Mesh(
      this.sharedInnerGlowGeometry,
      this.sharedInnerGlowMaterial
    );
    glowGroup.add(innerGlow);

    const outerGlow = new THREE.Mesh(
      this.sharedOuterGlowGeometry,
      this.sharedOuterGlowMaterial
    );
    glowGroup.add(outerGlow);

    this.scene.add(glowGroup);

    // Define collision bounds (centered on the laser beam)
    const bounds = {
      left: centerX - beamWidth / 2,
      right: centerX + beamWidth / 2,
      top: centerY + beamHeight / 2,
      bottom: centerY - beamHeight / 2,
    };

    // Default values for sparks if not created
    let sparks = [];
    let sparkY = y;

    // Find block intersection point for sparks
    if (createSparks) {
      // Find block intersection only if sparks are needed
      const blocksAtPosition = this.blockManager.getBlocksNearX(centerX, 2.0);
      sparkY = -5; // Default to base ground level
      let foundBlock = false;

      // Find the highest block y-position at the laser x
      for (const block of blocksAtPosition) {
        if (centerX >= block.bounds.left && centerX <= block.bounds.right) {
          if (block.bounds.top > sparkY) {
            sparkY = block.bounds.top;
            foundBlock = true;
          }
        }
      }

      // Fall back to checking all blocks if no intersection was found
      if (!foundBlock) {
        for (const block of this.blockManager.blocks) {
          if (centerX >= block.bounds.left && centerX <= block.bounds.right) {
            if (block.bounds.top > sparkY) {
              sparkY = block.bounds.top;
              foundBlock = true;
            }
          }
        }
      }

      // If no block found, use a fixed position above the bottom of the screen
      if (!foundBlock) {
        sparkY = -4.0; // Fallback position
      }
    }

    // Create a laser object with synchronized pulsing
    const laser = {
      beam,
      glow: glowGroup,
      sparks,
      bounds,
      x: centerX,
      y: centerY,
      sparkY: sparkY,
      hasSparks: createSparks,
      active: true,
      isActive: true,
      sparkSpawnTimer: 0,
      // Constant spark creation rate (per frame)
      sparkSpawnRate: 0.6, // Increased from 0.2 to 0.5 (50% chance per frame)
    };

    const isInitiallyOn = this.globalPhase < this.onDuration;
    laser.beam.visible = isInitiallyOn;
    laser.glow.visible = isInitiallyOn;
    laser.isActive = isInitiallyOn;

    this.lasers.push(laser);
    return laser;
  }

  // Create a HORIZONTAL laser segment occupying a single grid cell. Same shared
  // red beam/glow as a vertical laser, rotated so the beam runs left-to-right
  // across its cell. Authors string a row of `l` between two blocks (the
  // enclosure), so collision spans the full cell width and is thin vertically.
  // These share the global pulse + the same update()/checkCollision()/dispose()
  // paths as vertical lasers (they only read beam/glow/bounds/isActive). No
  // sparks — a horizontal beam has no single "block below" intersection point.
  createHorizontalLaser(x, y) {
    const cellSize = 0.5;
    const centerX = x + cellSize / 2;
    const centerY = y + cellSize / 2;

    const beamLength = this.beamHeight; // 0.5 — spans the cell horizontally
    const beamThickness = this.beamWidth; // 0.1 — thin vertical extent

    const beam = new THREE.Mesh(this.sharedHBeamGeometry, this.sharedBeamMaterial);
    beam.position.set(centerX, centerY, 0.02);
    beam.castShadow = false;
    beam.visible = true;
    this.scene.add(beam);

    const glowGroup = new THREE.Group();
    glowGroup.position.set(centerX, centerY, 0.01);
    glowGroup.visible = true;

    const innerGlow = new THREE.Mesh(
      this.sharedHInnerGlowGeometry,
      this.sharedInnerGlowMaterial
    );
    glowGroup.add(innerGlow);

    const outerGlow = new THREE.Mesh(
      this.sharedHOuterGlowGeometry,
      this.sharedOuterGlowMaterial
    );
    glowGroup.add(outerGlow);

    this.scene.add(glowGroup);

    // Collision bounds: full cell width, thin vertically (centered on the beam).
    const bounds = {
      left: centerX - beamLength / 2,
      right: centerX + beamLength / 2,
      top: centerY + beamThickness / 2,
      bottom: centerY - beamThickness / 2,
    };

    const laser = {
      beam,
      glow: glowGroup,
      sparks: [],
      bounds,
      x: centerX,
      y: centerY,
      sparkY: centerY,
      hasSparks: false,
      active: true,
      isActive: true,
      sparkSpawnTimer: 0,
      sparkSpawnRate: 0,
    };

    const isInitiallyOn = this.globalPhase < this.onDuration;
    laser.beam.visible = isInitiallyOn;
    laser.glow.visible = isInitiallyOn;
    laser.isActive = isInitiallyOn;

    this.lasers.push(laser);
    return laser;
  }

  // Create a single spark mesh
  createSparkMesh(x, y, size) {
    // Shared unit geometry/material, sized via scale (square spark).
    const mesh = new THREE.Mesh(
      this.sharedSparkGeometry,
      this.sharedSparkMaterial
    );
    mesh.scale.set(size, size, 1);

    // Slightly randomize the starting position at the intersection point
    const offsetX = (Math.random() - 0.5) * 0.05;
    const offsetY = Math.random() * 0.05;
    mesh.position.set(x + offsetX, y + offsetY, 0.2);

    // Add to scene
    this.scene.add(mesh);

    // Generate random direction and speed - much lower velocity as requested
    const angle = Math.random() * Math.PI * 2;
    const speed = Math.random() * 0.02 + 0.01;

    // Return spark object with all properties needed for animation
    // Shorter lifetime for faster shrinking
    const shorterLifetime = Math.random() * 0.5 + 0.3; // Reduced from 0.8+0.4 to 0.4+0.2
    return {
      mesh,
      size: size,
      originalSize: size,
      velocity: {
        x: Math.cos(angle) * speed,
        y: Math.sin(angle) * speed,
      },
      lifetime: shorterLifetime,
      maxLifetime: shorterLifetime,
    };
  }

  // Update all lasers with synchronized pulsing
  update(deltaGameSpeed) {
    // Update global phase
    this.globalPhase += deltaGameSpeed;

    // Keep global phase within cycle range
    while (this.globalPhase >= this.totalCycleDuration) {
      this.globalPhase -= this.totalCycleDuration;
    }

    // Determine global laser state
    const lasersOn = this.globalPhase < this.onDuration;

    for (const laser of this.lasers) {
      if (!laser.active) continue;

      // Set visibility based on global state
      if (lasersOn) {
        // Lasers are "on"
        if (!laser.beam.visible) {
          laser.beam.visible = true;
          laser.glow.visible = true;
          laser.isActive = true;
        }

        // Create new sparks only when laser is on
        if (laser.hasSparks) {
          // Try to spawn new sparks at a constant rate (potentially multiple per frame)
          // Increased spawn count
          const sparkSpawnCount = Math.floor(
            laser.sparkSpawnRate * deltaGameSpeed * 3
          ); // Multiplied by 3 for more sparks
          for (let i = 0; i < sparkSpawnCount; i++) {
            if (Math.random() < 0.7) {
              // 70% chance for each potential spawn
              const sparkSize = Math.random() * 0.08 + 0.03;
              const spark = this.createSparkMesh(
                laser.x,
                laser.sparkY,
                sparkSize
              );
              laser.sparks.push(spark);
            }
          }
        }
      } else {
        // Lasers are "off"
        if (laser.beam.visible) {
          laser.beam.visible = false;
          laser.glow.visible = false;
          laser.isActive = false;
        }
      }

      // Always update existing sparks whether laser is on or off
      if (laser.hasSparks && laser.sparks.length > 0) {
        this.updateSparks(laser, deltaGameSpeed);
      }
    }
  }

  // Update spark meshes for a laser
  updateSparks(laser, deltaGameSpeed) {
    // Update existing sparks
    for (let i = laser.sparks.length - 1; i >= 0; i--) {
      const spark = laser.sparks[i];

      // Update position
      spark.mesh.position.x += spark.velocity.x * deltaGameSpeed;
      spark.mesh.position.y += spark.velocity.y * deltaGameSpeed;

      // Update lifetime
      spark.lifetime -= deltaGameSpeed / 60;

      // Scale down the spark based on remaining lifetime. The shared geometry is
      // a unit square, so the on-screen size is originalSize * lifeRatio.
      const lifeRatio = spark.lifetime / spark.maxLifetime;
      const scaledSize = spark.originalSize * lifeRatio;
      spark.mesh.scale.set(scaledSize, scaledSize, 1);

      // If spark has expired, remove it
      if (spark.lifetime <= 0) {
        this.scene.remove(spark.mesh);
        laser.sparks.splice(i, 1);
      }
    }
  }

  // Get active laser bounds for collision detection
  getLaserBounds() {
    return this.lasers
      .filter((laser) => laser.isActive)
      .map((laser) => laser.bounds);
  }

  // Check player collision with lasers
  checkCollision(playerBounds) {
    for (const laser of this.lasers) {
      if (!laser.isActive) continue;

      // Basic AABB collision check
      if (
        playerBounds.left < laser.bounds.right &&
        playerBounds.right > laser.bounds.left &&
        playerBounds.top > laser.bounds.bottom &&
        playerBounds.bottom < laser.bounds.top
      ) {
        return true; // Collision detected
      }
    }

    return false; // No collision
  }

  // Internal helper to remove a laser's meshes from the scene. Geometry and
  // materials are shared across all lasers, so they are NOT disposed here — only
  // once in dispose() at teardown.
  disposeLaser(laser) {
    if (!laser) return;

    if (laser.beam) {
      this.scene.remove(laser.beam);
      laser.beam = null;
    }

    if (laser.glow) {
      this.scene.remove(laser.glow);
      laser.glow = null;
    }

    if (laser.sparks && laser.sparks.length > 0) {
      laser.sparks.forEach((spark) => {
        if (spark.mesh) {
          this.scene.remove(spark.mesh);
        }
      });
      laser.sparks = [];
    }

    if (laser.marker) {
      this.scene.remove(laser.marker);
      laser.marker = null;
    }
  }

  // Dispose shared GPU resources. Call once when the manager is torn down.
  dispose() {
    this.reset();
    this.sharedBeamGeometry.dispose();
    this.sharedInnerGlowGeometry.dispose();
    this.sharedOuterGlowGeometry.dispose();
    this.sharedHBeamGeometry.dispose();
    this.sharedHInnerGlowGeometry.dispose();
    this.sharedHOuterGlowGeometry.dispose();
    this.sharedSparkGeometry.dispose();
    this.sharedBeamMaterial.dispose();
    this.sharedInnerGlowMaterial.dispose();
    this.sharedOuterGlowMaterial.dispose();
    this.sharedSparkMaterial.dispose();
  }

  // Remove lasers whose x position is before a given coordinate
  removeLasersBeforeX(x) {
    for (let i = this.lasers.length - 1; i >= 0; i--) {
      const laser = this.lasers[i];
      // Check the laser's center x coordinate
      if (laser.x < x) {
        this.disposeLaser(laser);
        this.lasers.splice(i, 1);
      }
    }
  }
}
